import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import { TAB_VIEW } from "../../../pages/Workspace/WorkspaceConfiguration/constants";
import { MultiValueStackedBarChartData } from "./types";
import { calculatePercentage } from "../../../utils/numberUtils";

// Chart display constants
const CHART_DISPLAY_CONSTANTS = {
  /** Minimum percentage for total bar visibility (36%) */
  MIN_TOTAL_DISPLAY_PERCENTAGE: 36,
  /** Minimum percentage for current bar visibility (2%) to ensure bars are always visible */
  MIN_CURRENT_DISPLAY_PERCENTAGE: 2,
  /** Minimum percentage for potential bar visibility (2%) to ensure bars are always visible */
  MIN_POTENTIAL_DISPLAY_PERCENTAGE: 2,
  /** Maximum percentage cap for configured segments */
  MAX_CONFIGURED_PERCENTAGE: 100,
} as const;

const editIconSVGPath =
  "M2.249 12.938v2.813h2.813l8.294-8.295-2.812-2.813-8.295 8.296zm13.282-7.657a.747.747 0 0 0 0-1.058l-1.755-1.755a.747.747 0 0 0-1.057 0l-1.372 1.373 2.812 2.812 1.372-1.372z";
const warningIconSVGPath = "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z";
const smallWarningIconSVGPath =
  "M.667 14h14.666L8 1.333.667 14zm8-2H7.333v-1.333h1.334V12zm0-2.667H7.333V6.667h1.334v2.666z";

export const createCurrentSeries = (
  chart: am4charts.XYChart,
  onEditConfiguration: () => void
) => {
  const currentSeries = chart.series.push(new am4charts.ColumnSeries());
  currentSeries.dataFields.valueX =
    "displayCurrentPercentage" satisfies keyof MultiValueStackedBarChartData;
  currentSeries.dataFields.categoryY = "segmentName";
  currentSeries.name = "Current";
  currentSeries.stacked = true;
  currentSeries.clustered = false;
  currentSeries.columns.template.fill = am4core.color("#0288d1");
  currentSeries.columns.template.height = 14;
  currentSeries.columns.template.tooltip = new am4core.Tooltip();
  currentSeries.columns.template.tooltipText =
    "{segmentName}: {current.formatNumber('#,###')} Current";
  currentSeries.columns.template.tooltipPosition = "pointer";

  currentSeries.zIndex = 2;
  currentSeries.columns.template.stroke = am4core.color("#ffffff"); // White border for gap
  currentSeries.columns.template.strokeWidth = 2; // 2px gap
  currentSeries.columns.template.dy = 25;
  currentSeries.columns.template.height = 14;

  const currentLabelBullet = currentSeries.bullets.push(
    new am4charts.LabelBullet()
  );
  currentLabelBullet.label.text = "{current.formatNumber('#,###')} Current";
  currentLabelBullet.label.fill = am4core.color("#3a3a3a");
  currentLabelBullet.label.fontSize = 12;
  currentLabelBullet.label.verticalCenter = "top";
  currentLabelBullet.dy = 25 + 10;
  currentLabelBullet.locationX = 0.5; // Middle of the current segment
  currentLabelBullet.locationY = 0.5;
  currentLabelBullet.label.horizontalCenter = "left";
  currentLabelBullet.label.paddingLeft = 4;
  currentLabelBullet.label.truncate = true;
  currentLabelBullet.label.wrap = false;

  // Position label at the left edge of the segment and set max width for truncation
  currentLabelBullet.label.adapter.add("dx", (dx, target) => {
    const dataItem = target.dataItem as
      | am4charts.ColumnSeriesDataItem
      | undefined;
    if (dataItem?.column) {
      const columnWidth = dataItem.column.pixelWidth || 0;
      // Position at left edge: move left by half the column width, then add small padding
      return -(columnWidth / 2) + 4;
    }
    return dx;
  });

  // Set max width based on segment width for proper truncation
  currentLabelBullet.label.adapter.add("maxWidth", (maxWidth, target) => {
    const dataItem = target.dataItem as
      | am4charts.ColumnSeriesDataItem
      | undefined;
    if (dataItem?.column) {
      const columnWidth = dataItem.column.pixelWidth || 0;
      // Reserve padding and ensure minimum readable width
      const availableWidth = Math.max(columnWidth - 12, 40);

      // If segment is very narrow, hide the label entirely
      if (availableWidth < 60) {
        return 0;
      }

      return availableWidth;
    }
    return maxWidth;
  });

  currentLabelBullet.propertyFields.disabled =
    "hideConfiguredCurrentSeriesBullets";

  const iconBullet = currentSeries.bullets.push(new am4charts.Bullet());
  const iconSprite = iconBullet.createChild(am4core.Sprite);
  iconSprite.path = smallWarningIconSVGPath;
  iconSprite.fill = am4core.color("#ef5350");
  iconSprite.strokeWidth = 0;
  iconSprite.width = 16;
  iconSprite.height = 16;
  iconSprite.horizontalCenter = "middle";
  iconSprite.verticalCenter = "middle";
  iconBullet.locationX = 1;
  iconBullet.locationY = 0.5;
  iconBullet.dy = 25;
  iconBullet.dx = 25;
  iconBullet.propertyFields.disabled = "hideDisabledCurrentSeriesBullets";

  const updateConfigBullet = currentSeries.bullets.push(
    new am4charts.LabelBullet()
  );
  updateConfigBullet.label.text =
    "Insufficient Information. Please update the project configuration.";
  updateConfigBullet.label.fill = am4core.color("#3a3a3a");
  updateConfigBullet.label.fontSize = 12;
  updateConfigBullet.label.verticalCenter = "top";
  updateConfigBullet.label.horizontalCenter = "left";
  updateConfigBullet.label.truncate = false;
  updateConfigBullet.label.wrap = false;
  updateConfigBullet.label.paddingLeft = 4;
  updateConfigBullet.fontFamily = "Roboto";
  updateConfigBullet.opacity = 0.75;
  updateConfigBullet.dy = 19;
  updateConfigBullet.dx = 35;
  updateConfigBullet.locationX = 1;
  updateConfigBullet.locationY = 0.5;
  updateConfigBullet.propertyFields.disabled =
    "hideDisabledCurrentSeriesBullets";

  const link = currentSeries.bullets.push(new am4charts.LabelBullet());
  link.label.text = "Edit Configuration";
  link.label.fill = am4core.color("#1DA5C3");
  link.label.fontSize = 12;
  link.label.verticalCenter = "top";
  link.label.horizontalCenter = "left";
  link.label.truncate = false;
  link.label.wrap = false;
  link.label.paddingLeft = 4;
  link.fontFamily = "Roboto";
  link.textDecoration = "underline";
  link.dy = 19;
  link.dx = 400;
  link.locationX = 1;
  link.locationY = 0.5;
  link.propertyFields.disabled = "hideDisabledCurrentSeriesBullets";

  link.cursorOverStyle = am4core.MouseCursorStyle.pointer;
  link.interactionsEnabled = true;
  link.events.on("hit", () => {
    onEditConfiguration();
  });

  currentSeries.events.on("validated", () =>
    currentSeriesColorModification(currentSeries.dataItems)
  );
};

const createStackedSeries = (
  chart: am4charts.XYChart,
  options: {
    name: string;
    widthPercentage: keyof MultiValueStackedBarChartData;
    value: keyof MultiValueStackedBarChartData;
    color: string;
    index: number;
  }
) => {
  const potentialSeries = chart.series.push(new am4charts.ColumnSeries());
  potentialSeries.dataFields.valueX = options.widthPercentage;
  potentialSeries.dataFields.categoryY = "segmentName";
  potentialSeries.name = options.name;
  potentialSeries.stacked = true;
  potentialSeries.clustered = false;
  potentialSeries.columns.template.fill = am4core.color(options.color);
  potentialSeries.columns.template.height = 14;
  potentialSeries.columns.template.tooltip = new am4core.Tooltip();
  potentialSeries.columns.template.tooltipText = `{segmentName}: {${options.value}.formatNumber('#,###')} {name}`;
  potentialSeries.columns.template.tooltipPosition = "pointer";
  potentialSeries.zIndex = options.index;
  potentialSeries.columns.template.stroke = am4core.color("#ffffff"); // White border for gap
  potentialSeries.columns.template.strokeWidth = 2; // 2px gap
  potentialSeries.columns.template.dy = 25;
  const potentialLabelBullet = potentialSeries.bullets.push(
    new am4charts.LabelBullet()
  );

  potentialLabelBullet.label.adapter.add("text", (_, target) => {
    const dataContext = target.dataItem
      ?.dataContext as MultiValueStackedBarChartData;
    if (dataContext?.[options.widthPercentage] === undefined) {
      return "";
    }
    return `{${options.value}.formatNumber('#,###')} ${options.name}`;
  });
  potentialLabelBullet.label.fill = am4core.color("#3a3a3a");
  potentialLabelBullet.label.fontSize = 12;
  potentialLabelBullet.label.verticalCenter = "top";
  potentialLabelBullet.dy = 25 + 10;
  potentialLabelBullet.locationX = 0.5; // Middle of this segment
  potentialLabelBullet.locationY = 0.5;
  potentialLabelBullet.label.horizontalCenter = "left";
  potentialLabelBullet.label.paddingLeft = 4;
  potentialLabelBullet.label.truncate = true;
  potentialLabelBullet.label.wrap = false;

  // Position label at the left edge of the segment and set max width for truncation
  potentialLabelBullet.label.adapter.add("dx", (dx, target) => {
    const dataItem = target.dataItem as
      | am4charts.ColumnSeriesDataItem
      | undefined;
    if (dataItem?.column) {
      const columnWidth = dataItem.column.pixelWidth || 0;
      // Position at left edge: move left by half the column width, then add small padding
      return -(columnWidth / 2) + 4;
    }
    return dx;
  });

  // Set max width based on segment width for proper truncation
  potentialLabelBullet.label.adapter.add("maxWidth", (maxWidth, target) => {
    const dataItem = target.dataItem as
      | am4charts.ColumnSeriesDataItem
      | undefined;
    if (dataItem?.column) {
      const columnWidth = dataItem.column.pixelWidth || 0;
      // Reserve padding and ensure minimum readable width
      const availableWidth = Math.max(columnWidth - 12, 40);

      // If segment is very narrow, hide the label entirely
      if (availableWidth < 60) {
        return 0;
      }

      return availableWidth;
    }
    return maxWidth;
  });

  potentialSeries.events.on("validated", () =>
    potentialSeriesColorModification(potentialSeries.dataItems)
  );
};

export const createPotentialSeries = (
  chart: am4charts.XYChart,
  isHierarchyMatchingEnabled?: boolean
) => {
  if (isHierarchyMatchingEnabled) {
    createStackedSeries(chart, {
      name: "Expansion",
      widthPercentage: "displayExpansionPercentage",
      value: "expansion",
      color: "#ab47bc",
      index: 1,
    });

    createStackedSeries(chart, {
      name: "Prospect",
      widthPercentage: "displayProspectPercentage",
      value: "prospect",
      color: "#e1bee7",
      index: 2,
    });
  } else {
    createStackedSeries(chart, {
      name: "Whitespace",
      widthPercentage: "displayWhitespacePercentage",
      value: "whitespace",
      color: "#ffa726",
      index: 0,
    });
  }
};

export const createTotalSeries = (
  chart: am4charts.XYChart,
  onInsufficientButtonClick: (segment: TAB_VIEW) => void
) => {
  const totalSeries = chart.series.push(new am4charts.ColumnSeries());
  totalSeries.dataFields.valueX = "displayTotalPercentage";
  totalSeries.dataFields.categoryY = "segmentName";
  totalSeries.name = "Total";
  totalSeries.stacked = false;
  totalSeries.clustered = false;
  totalSeries.columns.template.fill = am4core.color("#1da5c3");
  totalSeries.columns.template.height = 50;
  totalSeries.zIndex = 3;
  totalSeries.columns.template.stroke = am4core.color("transparent");
  totalSeries.columns.template.strokeWidth = 0;
  totalSeries.columns.template.dy = -10;

  const pattern = new am4core.LinePattern();
  pattern.rotation = -45;
  pattern.stroke = am4core.color("#b2ebf2");
  pattern.strokeWidth = 2;
  pattern.width = 12;
  pattern.height = 12;
  pattern.backgroundFill = am4core.color("#1da5c3");
  pattern.backgroundOpacity = 0.4;

  totalSeries.columns.template.fill = pattern;

  const spendOverCompaniesBullet = totalSeries.bullets.push(
    new am4charts.LabelBullet()
  );
  spendOverCompaniesBullet.label.text =
    "[bold]$ {totalSpend.formatNumber('#.#a')} / {totalCompanies.formatNumber('#,###')} Companies[/]";
  spendOverCompaniesBullet.label.fill = am4core.color("#3a3a3a");
  spendOverCompaniesBullet.label.fontSize = 20;
  spendOverCompaniesBullet.label.verticalCenter = "middle";
  spendOverCompaniesBullet.locationX = 1;
  spendOverCompaniesBullet.locationY = 0.66;
  spendOverCompaniesBullet.label.horizontalCenter = "left";
  spendOverCompaniesBullet.label.paddingLeft = 10;
  spendOverCompaniesBullet.label.paddingRight = 60;
  spendOverCompaniesBullet.label.truncate = true;
  spendOverCompaniesBullet.label.wrap = false;
  spendOverCompaniesBullet.propertyFields.disabled =
    "hideConfiguredTotalSeriesBullets";

  const totalPercentageBullet = totalSeries.bullets.push(
    new am4charts.LabelBullet()
  );
  totalPercentageBullet.label.adapter.add("text", (_, target) => {
    const dataContext = target.dataItem
      ?.dataContext as MultiValueStackedBarChartData;
    const percentage = dataContext?.totalPercentage;

    if (percentage === undefined || percentage === null) return "";

    if (percentage < 1) {
      return "[bold]< 1%[/]";
    }

    return `[bold]${percentage}%[/]`;
  });
  totalPercentageBullet.label.fill = am4core.color("#3a3a3a");
  totalPercentageBullet.label.fontSize = 20;
  totalPercentageBullet.label.verticalCenter = "middle";
  totalPercentageBullet.locationY = 0.66;
  totalPercentageBullet.label.horizontalCenter = "right";
  totalPercentageBullet.label.truncate = false;
  totalPercentageBullet.label.wrap = false;
  totalPercentageBullet.label.paddingLeft = 10;
  totalPercentageBullet.label.paddingRight = 10;
  totalPercentageBullet.label.propertyFields.disabled =
    "hideConfiguredTotalSeriesBullets";

  const iconBullet = totalSeries.bullets.push(new am4charts.Bullet());
  const iconSprite = iconBullet.createChild(am4core.Sprite);
  iconSprite.path = warningIconSVGPath;
  iconSprite.fill = am4core.color("#ef5350");
  iconSprite.strokeWidth = 0;
  iconSprite.width = 24;
  iconSprite.height = 24;
  iconSprite.horizontalCenter = "middle";
  iconSprite.verticalCenter = "middle";
  iconBullet.locationX = 1;
  iconBullet.locationY = 0.5;
  iconBullet.dy = -10;
  iconBullet.dx = 25;
  iconBullet.propertyFields.disabled = "hideDisabledTotalSeriesBullets";

  const insufficientInfoBullet = totalSeries.bullets.push(
    new am4charts.LabelBullet()
  );
  insufficientInfoBullet.label.text = "Insufficient Information";
  insufficientInfoBullet.label.fill = am4core.color("#3a3a3a");
  insufficientInfoBullet.label.fontSize = 20;
  insufficientInfoBullet.label.verticalCenter = "middle";
  insufficientInfoBullet.label.horizontalCenter = "left";
  insufficientInfoBullet.locationX = 1;
  insufficientInfoBullet.locationY = 0.5;
  insufficientInfoBullet.label.dy = -15;
  insufficientInfoBullet.label.dx = 35;
  insufficientInfoBullet.label.paddingLeft = 10;
  insufficientInfoBullet.label.paddingRight = 60;
  insufficientInfoBullet.label.truncate = false;
  insufficientInfoBullet.label.wrap = false;
  insufficientInfoBullet.label.fontWeight = "bold";
  insufficientInfoBullet.label.fontFamily = "Roboto";
  insufficientInfoBullet.propertyFields.disabled =
    "hideDisabledTotalSeriesBullets";

  const updateConfigBullet = totalSeries.bullets.push(
    new am4charts.LabelBullet()
  );
  updateConfigBullet.label.text = "Please update project configuration.";
  updateConfigBullet.label.fill = am4core.color("#3a3a3a");
  updateConfigBullet.label.fontSize = 12;
  updateConfigBullet.label.verticalCenter = "middle";
  updateConfigBullet.label.horizontalCenter = "left";
  updateConfigBullet.locationX = 1;
  updateConfigBullet.locationY = 0.5;
  updateConfigBullet.label.dy = 4;
  updateConfigBullet.label.dx = 35;
  updateConfigBullet.label.paddingLeft = 10;
  updateConfigBullet.label.paddingRight = 10;
  updateConfigBullet.label.truncate = false;
  updateConfigBullet.label.wrap = false;
  updateConfigBullet.label.fontFamily = "Roboto";
  updateConfigBullet.label.opacity = 0.75;
  updateConfigBullet.propertyFields.disabled = "hideDisabledTotalSeriesBullets";

  const buttonBullet = totalSeries.bullets.push(new am4charts.Bullet());
  const button = buttonBullet.createChild(am4core.Button);
  button.width = 180;
  button.horizontalCenter = "right";
  button.verticalCenter = "middle";
  buttonBullet.dy = -10;
  buttonBullet.dx = -20;
  buttonBullet.propertyFields.disabled = "hideDisabledTotalSeriesBullets";

  if (button.label) {
    button.label.fontSize = 13;
    button.label.fontFamily = "Roboto";
    button.label.fill = am4core.color("#ffffff");
    button.label.strokeWidth = 0;
    button.label.marginLeft = 7.5;
    button.label.verticalCenter = "middle";
    button.label.horizontalCenter = "middle";
    button.label.text = "EDIT CONFIGURATION";
  }
  button.padding(5, 10, 5, 10);
  button.background.fill = am4core.color("#748291");
  button.icon = new am4core.Sprite();
  button.icon.path = editIconSVGPath;
  button.icon.fill = am4core.color("#ffffff");
  button.icon.strokeWidth = 0;
  button.cursorOverStyle = am4core.MouseCursorStyle.pointer;
  button.events.on("hit", (events) => {
    const currentItem = events.target.dataItem?.dataContext as
      | MultiValueStackedBarChartData
      | undefined;

    onInsufficientButtonClick(
      currentItem?.segmentName === "SAM" ? TAB_VIEW.SAM : TAB_VIEW.SOM
    );
  });

  totalSeries.events.on("validated", () =>
    totalSeriesColorModification(totalSeries.dataItems)
  );
};

export const configureLegend = (
  chart: am4charts.XYChart,
  legendItems?: Array<{ name: string; color: string }>,
  options?: {
    interactionDisabled?: boolean;
    position?: "top" | "bottom" | "left" | "right";
    align?: "left" | "center" | "right";
  }
): am4charts.Legend | undefined => {
  if (!legendItems || legendItems.length === 0) {
    return;
  }

  const legend = new am4charts.Legend();
  chart.legend = legend;

  legend.useDefaultMarker = true;
  legend.position = options?.position || "top";
  legend.contentAlign = options?.align || "right";
  legend.fontSize = 12;
  legend.fontWeight = "500";
  legend.markers.template.width = 16;
  legend.markers.template.height = 4;

  // Align legend items and labels vertically
  legend.itemContainers.template.valign = "middle";
  legend.labels.template.valign = "middle";
  legend.labels.template.dy = 1; // Small adjustment to center text with marker

  // Disable interactions if requested
  if (options?.interactionDisabled) {
    legend.itemContainers.template.clickable = false;
    legend.itemContainers.template.focusable = false;
    legend.itemContainers.template.cursorOverStyle =
      am4core.MouseCursorStyle.default;
    legend.itemContainers.template.togglable = false;
  }

  legend.data = legendItems.map((item) => ({
    name: item.name.toUpperCase(),
    fill: am4core.color(item.color),
  }));

  return legend;
};

export const configureAxes = (chart: am4charts.XYChart) => {
  const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
  categoryAxis.dataFields.category = "segmentName";
  categoryAxis.renderer.inversed = true;
  categoryAxis.renderer.grid.template.disabled = true;
  categoryAxis.renderer.labels.template.fontSize = 20;
  categoryAxis.renderer.labels.template.fill = am4core.color("#757575");

  const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
  valueAxis.min = 0;
  valueAxis.max = 100;
  valueAxis.strictMinMax = true;
  valueAxis.calculateTotals = true;
  valueAxis.renderer.grid.template.disabled = true;
  valueAxis.renderer.baseGrid.disabled = true;
  valueAxis.renderer.labels.template.disabled = true;
};

/**
 * Check if a chart data item should be hidden (when ALL data values are 0)
 * A row should only be hidden when it's configured but has no data.
 * Unconfigured segments should still be shown with configuration prompts.
 */
function shouldHideChartRow(item: MultiValueStackedBarChartData): boolean {
  // Never hide unconfigured segments - they should show configuration prompts
  if (!item.isSegmentConfigured) {
    return false;
  }

  // Only hide configured segments that have no data
  return (
    item.totalCompanies === 0 &&
    item.current === 0 &&
    (item.potential === undefined || item.potential === 0) &&
    item.totalSpend === 0
  );
}

export const processMultiValueStacketBarChartData = (
  data: MultiValueStackedBarChartData[],
  isHierarchyMatchingEnabled?: boolean
): MultiValueStackedBarChartData[] => {
  // Filter out rows where all data is 0
  const filteredData = data.filter((item) => !shouldHideChartRow(item));

  // If no data remains after filtering, return empty array
  if (filteredData.length === 0) {
    return [];
  }

  const maxCompanies = Math.max(
    ...filteredData.map((item) => item.totalCompanies)
  );

  return filteredData.map((item: MultiValueStackedBarChartData) => {
    const percentages = calculateSeriesPercentages(
      item,
      maxCompanies,
      isHierarchyMatchingEnabled
    );
    const visibilityFlags = calculateVisibilityFlags(
      item.isSegmentConfigured ?? false,
      item.isAdditionalInfoConfigured ?? false
    );

    return {
      ...item,
      ...percentages,
      ...visibilityFlags,
    };
  });
};

function totalSeriesColorModification(
  dataItems: am4core.OrderedListTemplate<am4charts.ColumnSeriesDataItem>
) {
  return dataItems.each((dataItem) => {
    const currentItem = dataItem.dataContext as MultiValueStackedBarChartData;
    const column = dataItem.column as am4charts.Column;

    if (!currentItem.isSegmentConfigured) {
      column.fill = am4core.color("#f5f5f5");
    }
  });
}

function potentialSeriesColorModification(
  dataItems: am4core.OrderedListTemplate<am4charts.ColumnSeriesDataItem>
) {
  return dataItems.each((dataItem) => {
    const currentItem = dataItem.dataContext as MultiValueStackedBarChartData;

    if (
      !currentItem.isSegmentConfigured ||
      !currentItem.isAdditionalInfoConfigured
    ) {
      dataItem.bullets.clear();
    }
  });
}

function currentSeriesColorModification(
  dataItems: am4core.OrderedListTemplate<am4charts.ColumnSeriesDataItem>
) {
  dataItems.each((dataItem) => {
    const currentItem = dataItem.dataContext as MultiValueStackedBarChartData;
    const column = dataItem.column as am4charts.Column;

    if (
      !currentItem.isSegmentConfigured ||
      !currentItem.isAdditionalInfoConfigured
    ) {
      column.fill = am4core.color("#f5f5f5");
    }
  });
}

function calculateSeriesPercentages(
  item: MultiValueStackedBarChartData,
  maxCompanies: number,
  isHierarchyMatchingEnabled?: boolean
) {
  const totalPercentage = calculatePercentage(
    item.totalCompanies,
    maxCompanies
  );
  const currentPercentage = calculatePercentage(
    item.current,
    item.totalCompanies
  );

  // Check if we have neither whitespace nor expansion/prospect data
  const shouldEarlyReturn =
    item.whitespace === undefined &&
    item.expansion === undefined &&
    item.prospect === undefined;

  if (shouldEarlyReturn) {
    const displayTotalPercentage = item.isSegmentConfigured
      ? Math.max(
          totalPercentage,
          CHART_DISPLAY_CONSTANTS.MIN_TOTAL_DISPLAY_PERCENTAGE
        )
      : CHART_DISPLAY_CONSTANTS.MAX_CONFIGURED_PERCENTAGE;

    const displayCurrentPercentage =
      !item.isSegmentConfigured || !item.isAdditionalInfoConfigured
        ? CHART_DISPLAY_CONSTANTS.MAX_CONFIGURED_PERCENTAGE
        : Math.max(
            Math.round((currentPercentage / 100) * displayTotalPercentage),
            CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
          );

    return {
      totalPercentage,
      currentPercentage,
      whitespacePercentage: 0,
      displayTotalPercentage,
      displayCurrentPercentage,
      displayWhitespacePercentage: 0,
      displayProspectPercentage: 0,
      displayExpansionPercentage: 0,
    } satisfies Partial<MultiValueStackedBarChartData>;
  }

  const expansionPercentage =
    item.totalCompanies === 0
      ? 0
      : Math.round(((item.expansion ?? 0) / item.totalCompanies) * 100);

  const prospectPercentage =
    item.totalCompanies === 0
      ? 0
      : Math.round(((item.prospect ?? 0) / item.totalCompanies) * 100);

  const displayTotal = item.isSegmentConfigured
    ? Math.max(
        totalPercentage,
        CHART_DISPLAY_CONSTANTS.MIN_TOTAL_DISPLAY_PERCENTAGE
      )
    : CHART_DISPLAY_CONSTANTS.MAX_CONFIGURED_PERCENTAGE;

  const whitespacePercentage =
    item.totalCompanies === 0
      ? 0
      : Math.round(((item.whitespace ?? 0) / item.totalCompanies) * 100);

  const potentialPercentage =
    item.totalCompanies === 0
      ? 0
      : Math.round(((item.potential ?? 0) / item.totalCompanies) * 100);

  const rawCurrentCalc = Math.round((currentPercentage / 100) * displayTotal);
  const displayCurrent =
    !item.isSegmentConfigured || !item.isAdditionalInfoConfigured
      ? CHART_DISPLAY_CONSTANTS.MAX_CONFIGURED_PERCENTAGE
      : Math.max(
          rawCurrentCalc,
          CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
        );

  // Calculate potential display percentage
  // When potential is defined (even if 0), ensure minimum width for visibility
  const rawPotentialCalc = Math.round(
    (potentialPercentage / 100) * displayTotal
  );

  // For potential bar: only use when we DON'T have expansion/prospect data OR when hierarchy matching is disabled
  // If hierarchy matching is enabled AND we have expansion/prospect, those bars will be used instead
  // Use the same logic as the chart component: hierarchy matching is only enabled if both whitespace is configured AND hierarchy classification is enabled
  const isWhitespaceConfigured = item.isAdditionalInfoConfigured;
  const effectiveHierarchyMatchingEnabled = Boolean(
    isWhitespaceConfigured && isHierarchyMatchingEnabled
  );
  const hasExpansionProspectData =
    effectiveHierarchyMatchingEnabled === true &&
    (item.expansion !== undefined || item.prospect !== undefined);

  const displayPotential =
    !item.isSegmentConfigured || !item.isAdditionalInfoConfigured
      ? 0
      : hasExpansionProspectData
        ? 0 // Don't use potential bar when we have expansion/prospect
        : item.potential !== undefined
          ? Math.max(
              rawPotentialCalc,
              CHART_DISPLAY_CONSTANTS.MIN_POTENTIAL_DISPLAY_PERCENTAGE
            )
          : // When in whitespace mode (no expansion/prospect), ensure minimum width for whitespace
            Math.max(
              displayTotal - displayCurrent,
              CHART_DISPLAY_CONSTANTS.MIN_POTENTIAL_DISPLAY_PERCENTAGE
            );

  // Adjust bars if they would exceed total, but ensure both have minimum width
  let adjustedDisplayCurrent = displayCurrent;
  let adjustedDisplayPotential = displayPotential;

  // Only adjust current + potential if we're NOT using expansion/prospect bars
  if (
    !hasExpansionProspectData &&
    displayPotential > 0 &&
    displayCurrent + displayPotential > displayTotal
  ) {
    // Both bars need minimum width, so distribute the remaining space proportionally
    const minCurrentWidth =
      item.current > 0
        ? CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
        : 0;
    const minPotentialWidth =
      item.potential !== undefined
        ? CHART_DISPLAY_CONSTANTS.MIN_POTENTIAL_DISPLAY_PERCENTAGE
        : 0;
    const totalMinWidth = minCurrentWidth + minPotentialWidth;

    if (totalMinWidth <= displayTotal) {
      // We can fit both minimum widths
      const remainingSpace = displayTotal - totalMinWidth;
      const originalTotal = displayCurrent + displayPotential;

      // Distribute remaining space proportionally
      const currentProportion = displayCurrent / originalTotal;
      const potentialProportion = displayPotential / originalTotal;

      adjustedDisplayCurrent =
        minCurrentWidth + remainingSpace * currentProportion;
      adjustedDisplayPotential =
        minPotentialWidth + remainingSpace * potentialProportion;
    } else {
      // Can't fit both minimum widths, prioritize based on data
      if (item.current > 0 && item.potential !== undefined) {
        // Both have data, split evenly
        adjustedDisplayCurrent = displayTotal / 2;
        adjustedDisplayPotential = displayTotal / 2;
      } else if (item.current > 0) {
        // Only current has data
        adjustedDisplayCurrent = displayTotal;
        adjustedDisplayPotential = 0;
      } else {
        // Only potential has data
        adjustedDisplayCurrent = 0;
        adjustedDisplayPotential = displayTotal;
      }
    }
  }

  // Calculate raw percentages first
  const rawExpansionPercentage =
    !item.isSegmentConfigured || !item.isAdditionalInfoConfigured
      ? 0
      : Math.round((expansionPercentage / 100) * displayTotal);

  const rawProspectPercentage =
    !item.isSegmentConfigured || !item.isAdditionalInfoConfigured
      ? 0
      : Math.round((prospectPercentage / 100) * displayTotal);

  // Apply minimum widths for visibility - ensure expansion and prospect always get min width when they have data
  let finalDisplayExpansionPercentage = 0;
  let finalDisplayProspectPercentage = 0;

  // Calculate available space for expansion/prospect after current bar
  const availableSpace = displayTotal - displayCurrent;

  // Calculate total expansion + prospect percentage to see if we need to scale
  const totalExpansionProspectPercentage =
    expansionPercentage + prospectPercentage;

  if (
    item.isSegmentConfigured &&
    item.isAdditionalInfoConfigured &&
    hasExpansionProspectData
  ) {
    // Calculate minimum widths needed - only when expansion/prospect bars should be visible
    const expansionMinWidth =
      item.expansion !== undefined
        ? CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
        : 0;
    const prospectMinWidth =
      item.prospect !== undefined
        ? CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
        : 0;
    const totalMinWidth = expansionMinWidth + prospectMinWidth;

    if (totalExpansionProspectPercentage > 0 || totalMinWidth > 0) {
      // Always apply minimum widths for bars that should be visible
      // This ensures bars are always visible even when filtered to 0 values
      if (item.expansion !== undefined) {
        finalDisplayExpansionPercentage = Math.max(
          rawExpansionPercentage,
          CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
        );
      }

      if (item.prospect !== undefined) {
        finalDisplayProspectPercentage = Math.max(
          rawProspectPercentage,
          CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
        );
      }

      // Only scale down if we have actual data (not just minimum widths from 0 values)
      // and if the total significantly exceeds available space
      const totalNeeded =
        finalDisplayExpansionPercentage + finalDisplayProspectPercentage;
      const hasActualData =
        rawExpansionPercentage > 0 || rawProspectPercentage > 0;

      if (hasActualData && totalNeeded > availableSpace && availableSpace > 0) {
        // Scale down proportionally while preserving minimum widths
        if (totalMinWidth <= availableSpace) {
          // We can fit minimum widths, scale the excess proportionally
          const excessExpansion = Math.max(
            0,
            finalDisplayExpansionPercentage -
              CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
          );
          const excessProspect = Math.max(
            0,
            finalDisplayProspectPercentage -
              CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
          );
          const totalExcess = excessExpansion + excessProspect;
          const availableForExcess = availableSpace - totalMinWidth;

          if (totalExcess > 0 && availableForExcess >= 0) {
            const excessScaleFactor = availableForExcess / totalExcess;
            finalDisplayExpansionPercentage =
              CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE +
              excessExpansion * excessScaleFactor;
            finalDisplayProspectPercentage =
              CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE +
              excessProspect * excessScaleFactor;
          }
        } else {
          // Not enough space for both minimums, but still preserve minimum widths
          // This allows the chart to exceed 100% to show all bars
          // Keep the minimum widths as calculated above
        }
      }
    }
  }

  // If total exceeds displayTotal, scale down while preserving minimums
  // For expansion/prospect mode, we need to fit: current + expansion + prospect within displayTotal
  const stackedTotal =
    displayCurrent +
    finalDisplayExpansionPercentage +
    finalDisplayProspectPercentage;

  // Handle overflow by reducing current bar to make room for minimum widths
  let finalDisplayCurrent = adjustedDisplayCurrent;

  if (stackedTotal > displayTotal) {
    const excess = stackedTotal - displayTotal;
    const availableToReduceFromExpansionProspect =
      Math.max(
        0,
        finalDisplayExpansionPercentage -
          CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
      ) +
      Math.max(
        0,
        finalDisplayProspectPercentage -
          CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
      );

    if (availableToReduceFromExpansionProspect > 0) {
      // First try to reduce expansion/prospect bars while preserving minimums
      const reductionRatio = Math.min(
        1,
        excess / availableToReduceFromExpansionProspect
      );
      finalDisplayExpansionPercentage =
        item.expansion !== undefined
          ? Math.max(
              CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE,
              finalDisplayExpansionPercentage -
                Math.max(
                  0,
                  finalDisplayExpansionPercentage -
                    CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
                ) *
                  reductionRatio
            )
          : 0;
      finalDisplayProspectPercentage =
        item.prospect !== undefined
          ? Math.max(
              CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE,
              finalDisplayProspectPercentage -
                Math.max(
                  0,
                  finalDisplayProspectPercentage -
                    CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE
                ) *
                  reductionRatio
            )
          : 0;
    } else {
      // If expansion/prospect are already at minimum, reduce current bar instead
      finalDisplayCurrent = Math.max(
        CHART_DISPLAY_CONSTANTS.MIN_CURRENT_DISPLAY_PERCENTAGE,
        adjustedDisplayCurrent - excess
      );
    }
  }

  const finalResult = {
    totalPercentage,
    currentPercentage,
    whitespacePercentage,
    expansionPercentage,
    prospectPercentage,
    potentialPercentage,
    displayTotalPercentage: displayTotal,
    displayCurrentPercentage: finalDisplayCurrent,
    displayPotentialPercentage: adjustedDisplayPotential,
    // Use displayPotential for whitespace only if whitespace is defined, otherwise 0
    displayWhitespacePercentage:
      item.whitespace !== undefined ? displayPotential : 0,
    displayProspectPercentage: finalDisplayProspectPercentage,
    displayExpansionPercentage: finalDisplayExpansionPercentage,
  } satisfies Partial<MultiValueStackedBarChartData>;

  return finalResult;
}

function calculateVisibilityFlags(
  isSegmentConfigured: boolean,
  isAdditionalInfoConfigured: boolean
) {
  return {
    hideConfiguredTotalSeriesBullets: !isSegmentConfigured,
    hideDisabledTotalSeriesBullets: isSegmentConfigured,
    hideConfiguredCurrentSeriesBullets:
      !isSegmentConfigured || !isAdditionalInfoConfigured,
    hideDisabledCurrentSeriesBullets:
      !isSegmentConfigured || isAdditionalInfoConfigured,
  };
}
